/**
 * HorizontalScrollCarousel - Horizontal scrolling container with snap-to-center behavior
 */

import { useRef, useEffect, forwardRef, useImperativeHandle, Children } from 'react'
import { cn } from '@/lib/style'
import { useScrollSnap } from '@/hooks/common/useScrollSnap'
import { CAROUSEL_SCROLL_DISTANCE } from '@/overlays/constants/dimensions'

interface HorizontalScrollCarouselProps {
  children: React.ReactNode
  onCenterChange?: (index: number) => void
  className?: string
  style?: React.CSSProperties
}

export interface HorizontalScrollCarouselRef {
  scrollToIndex: (index: number) => void
  getCenteredIndex: () => number
}

export const HorizontalScrollCarousel = forwardRef<
  HorizontalScrollCarouselRef,
  HorizontalScrollCarouselProps
>(({ children, onCenterChange, className, style }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const childCount = Children.count(children)

  const { centeredIndex, scrollToIndex } = useScrollSnap({
    containerRef,
    itemCount: childCount,
    onCenterChange,
  })

  useImperativeHandle(ref, () => ({
    scrollToIndex,
    getCenteredIndex: () => centeredIndex,
  }))

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const container = containerRef.current
      if (!container) return

      if (!container.contains(document.activeElement)) return

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          container.scrollBy({ left: -CAROUSEL_SCROLL_DISTANCE, behavior: 'smooth' })
          break
        case 'ArrowRight':
          e.preventDefault()
          container.scrollBy({ left: CAROUSEL_SCROLL_DISTANCE, behavior: 'smooth' })
          break
        case 'Home':
          e.preventDefault()
          container.scrollTo({ left: 0, behavior: 'smooth' })
          break
        case 'End':
          e.preventDefault()
          container.scrollTo({
            left: container.scrollWidth,
            behavior: 'smooth',
          })
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex overflow-x-auto',
        'scroll-snap-type-x scroll-snap-type-mandatory',
        'scroll-smooth',
        'scrollbar-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
      role="region"
      aria-label="Trip carousel"
      tabIndex={0}
      style={{
        scrollSnapType: 'x mandatory',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        ...style,
      }}
    >
      {children}
    </div>
  )
})

HorizontalScrollCarousel.displayName = 'HorizontalScrollCarousel'
